/**
 * Admin pages: Users, Departments, Manage Agents, Data Governance (scopes),
 * Feedback review. Keys are namespaced 'admin.*'; the Arabic table must cover
 * every key.
 */
const en = {
  // ── Shared admin vocabulary ───────────────────────────────────────
  'admin.role.Admin': 'Admin',
  'admin.role.User': 'User',
  'admin.allStatus': 'All Status',
  'admin.filterByStatus': 'Filter by status',
  'admin.operationFailed': 'Operation failed',
  'admin.noDepartment': 'No department',
  'admin.createdCol': 'Created',
  'admin.statusCol': 'Status',
  'admin.nameCol': 'Name',
  'admin.descriptionCol': 'Description',

  // ── Users ─────────────────────────────────────────────────────────
  'admin.users.title': 'User Management',
  'admin.users.subtitle': 'Manage system users and their permissions',
  'admin.users.add': '+ Add New User',
  'admin.users.searchPlaceholder': 'Search by name or email',
  'admin.users.searchLabel': 'Search users by name or email',
  'admin.users.filterRole': 'Filter by role',
  'admin.users.allRoles': 'All Roles',
  'admin.users.filterDepartment': 'Filter by department',
  'admin.users.allDepartments': 'All Departments',
  'admin.users.loading': 'Loading users',
  'admin.users.loadFailed': 'Failed to load users.',
  'admin.users.noMatch': 'No users match these filters.',
  'admin.users.none': 'No users yet.',
  'admin.users.addFirst': '+ Add the first user',
  'admin.users.colEmail': 'Email',
  'admin.users.colRole': 'Role',
  'admin.users.colDepartment': 'Department',
  'admin.users.edit': 'Edit {name}',
  'admin.users.activate': 'Activate',
  'admin.users.deactivate': 'Deactivate',
  'admin.users.activateName': 'Activate {name}',
  'admin.users.deactivateName': 'Deactivate {name}',
  'admin.users.deleteName': 'Delete {name}',
  'admin.users.noun': 'users',
  'admin.users.editTitle': 'Edit User',
  'admin.users.addTitle': 'Add New User',
  'admin.users.fullName': 'Full Name',
  'admin.users.email': 'Email',
  'admin.users.password': 'Password',
  'admin.users.passwordKeep': '(leave blank to keep current)',
  'admin.users.passwordHint': 'At least 6 characters.',
  'admin.users.role': 'Role',
  'admin.users.department': 'Department',
  'admin.users.activeCheckbox': 'Active',
  // Feature access (user dialog)
  'admin.access.selfNote': "You can't change your own access to tasks, workflows or connectors. Another administrator can.",
  'admin.access.adminHideNote': 'This is an administrator. Administrators get these features by default; untick one to hide it from them.',
  'admin.connectors.section': 'Connectors (MCP)',
  'admin.connectors.enable': 'Allow this user to connect MCP servers (Google Drive, OneDrive, custom) for agents to use',
  'admin.connectors.hint': 'They add and manage connectors from the chat composer. Turning access off keeps their saved connectors, but agents and tasks stop using them.',
  'admin.connectors.inUse': '{n} saved connector(s).',
  'admin.connectors.updateFailed': 'The user was saved, but connector access could not be updated.',
  'admin.users.update': 'Update User',
  'admin.users.create': 'Create User',
  'admin.users.updatedTitle': 'User updated',
  'admin.users.updatedBody': '{name} was updated.',
  'admin.users.createdTitle': 'User created',
  'admin.users.createdBody': '{name} can now sign in.',
  'admin.users.deleteTitle': 'Delete {name}?',
  'admin.users.deleteMessage': 'This account will be removed from the system.',
  'admin.users.deleteConsequence': 'They lose access to every agent immediately and can no longer sign in. This cannot be undone.',
  'admin.users.deleteConfirm': 'Delete user',
  'admin.users.deletedTitle': 'User deleted',
  'admin.users.deletedBody': '{name} was deleted.',
  'admin.users.deleteFailed': 'Failed to delete user',
  'admin.users.deactivateTitle': 'Deactivate {name}?',
  'admin.users.deactivateMessage': 'They will be signed out and blocked from signing in until reactivated.',
  'admin.users.nowActive': '{name} is now active.',
  'admin.users.nowInactive': '{name} is now inactive.',
  'admin.users.statusFailed': 'Failed to update status',

  'admin.depts.title': 'Departments',
  'admin.depts.subtitle': 'Organize people into departments and see who belongs to each',
  'admin.depts.add': '+ Add Department',
  'admin.depts.search': 'Search departments',
  'admin.depts.loadFailed': 'Failed to load departments.',
  'admin.depts.loading': 'Loading departments',
  'admin.depts.noMatch': 'No departments match these filters.',
  'admin.depts.none': 'No departments yet. Create one to start organizing users.',
  'admin.depts.addFirst': '+ Add the first department',
  'admin.depts.colDepartment': 'Department',
  'admin.depts.colManager': 'Manager',
  'admin.depts.colMembers': 'Members',
  'admin.depts.viewMembers': 'View members',
  'admin.depts.memberOne': '{n} member',
  'admin.depts.memberMany': '{n} members',
  'admin.depts.showMembers': 'Show members of {name}',
  'admin.depts.hideMembers': 'Hide members of {name}',
  'admin.depts.editName': 'Edit {name}',
  'admin.depts.deleteName': 'Delete {name}',
  'admin.depts.loadingMembers': 'Loading members',
  'admin.depts.noMembers': 'No users assigned to this department.',
  'admin.depts.noun': 'departments',
  'admin.depts.editTitle': 'Edit Department',
  'admin.depts.addTitle': 'Add Department',
  'admin.depts.nameLabel': 'Department Name',
  'admin.depts.namePlaceholder': 'e.g. IT, Finance, HR',
  'admin.depts.descLabel': 'Description',
  'admin.depts.descPlaceholder': 'Optional short description',
  'admin.depts.managerLabel': 'Manager (optional)',
  'admin.depts.noManager': 'No manager',
  'admin.depts.update': 'Update Department',
  'admin.depts.create': 'Create Department',
  'admin.depts.updatedTitle': 'Department updated',
  'admin.depts.updatedBody': '{name} was updated.',
  'admin.depts.createdTitle': 'Department created',
  'admin.depts.createdBody': '{name} was created.',
  'admin.depts.deleteTitle': 'Delete "{name}"?',
  'admin.depts.deleteMessage': 'The department will be removed from the organisation structure.',
  'admin.depts.deleteConsequenceOne': 'Its 1 member will be left unassigned. The person is not deleted, but loses any access granted through this department.',
  'admin.depts.deleteConsequenceMany': 'Its {n} members will be left unassigned. The people themselves are not deleted, but they lose any access granted through this department.',
  'admin.depts.deleteConfirm': 'Delete department',
  'admin.depts.deletedTitle': 'Department deleted',
  'admin.depts.deletedBody': '{name} was deleted.',
  'admin.depts.deleteFailed': 'Failed to delete department',
  'admin.depts.membersFailed': 'Failed to load department members',

  'admin.fb.range7': 'Last 7 days',
  'admin.fb.range30': 'Last 30 days',
  'admin.fb.range90': 'Last 90 days',
  'admin.fb.range365': 'Last year',
  'admin.fb.timeRange': 'Time range',
  'admin.fb.tabNeedsReview': 'Needs review',
  'admin.fb.tabNegative': 'Negative',
  'admin.fb.tabPositive': 'Positive',
  'admin.fb.tabReviewed': 'Reviewed',
  'admin.fb.tabAll': 'All',
  'admin.fb.negative': 'Negative',
  'admin.fb.positive': 'Positive',
  'admin.fb.question': 'Question',
  'admin.fb.response': 'Response',
  'admin.fb.showLess': 'Show less',
  'admin.fb.showFull': 'Show full response',
  'admin.fb.reviewed': 'Reviewed',
  'admin.fb.reviewedBy': 'Reviewed by {name}',
  'admin.fb.reopen': 'Reopen',
  'admin.fb.noteLabel': 'Review note',
  'admin.fb.notePlaceholder': 'Optional note (e.g. prompt unclear, source missing)…',
  'admin.fb.markReviewed': 'Mark reviewed',
  'admin.fb.loadFailed': 'Failed to load feedback.',
  'admin.fb.markedToast': 'Marked as reviewed.',
  'admin.fb.reopenedToast': 'Reopened for review.',
  'admin.fb.updateFailed': 'Could not update the review.',
  'admin.fb.title': 'Feedback review',
  'admin.fb.subtitle': 'Read what users rated, and track which negatives have been looked at',
  'admin.fb.tablist': 'Feedback filter',
  'admin.fb.searchPlaceholder': 'Search question, response or user…',
  'admin.fb.searchLabel': 'Search feedback',
  'admin.fb.filterAgent': 'Filter by agent',
  'admin.fb.allAgents': 'All agents',
  'admin.fb.loading': 'Loading feedback',
  'admin.fb.noMatch': 'No feedback matches these filters.',
  'admin.fb.nothingToReview': 'Nothing needs review in this range.',
  'admin.fb.none': 'No feedback in this range.',
  'admin.fb.noun': 'ratings',
  'admin.fb.chart': '[chart]',


  'admin.icon.auto': 'Auto (from name)',
  'admin.icon.autoTag': 'Auto',
  'admin.icon.sparkles': 'General',
  'admin.icon.scale': 'Legal',
  'admin.icon.shield': 'Security',
  'admin.icon.landmark': 'Finance',
  'admin.icon.calculator': 'Accounting',
  'admin.icon.brain': 'Knowledge',
  'admin.icon.hardhat': 'Safety',
  'admin.icon.users': 'People / HR',
  'admin.icon.cart': 'Procurement',
  'admin.icon.truck': 'Logistics',
  'admin.icon.wrench': 'Maintenance',
  'admin.icon.building': 'Projects',
  'admin.icon.megaphone': 'Marketing',
  'admin.icon.graduation': 'Training',
  'admin.icon.heart': 'Wellness',
  'admin.icon.file': 'Documents',
  'admin.icon.chart': 'Analytics',
  'admin.icon.globe': 'International',
  'admin.icon.briefcase': 'Business',
  'admin.icon.headset': 'Support',
  'admin.icon.code': 'Development',
  'admin.icon.database': 'Data',

  'admin.agents.title': 'Agent Management',
  'admin.agents.subtitle': 'Manage AI agents and user access',
  'admin.agents.add': '+ Add New Agent',
  'admin.agents.tabAgents': 'Agents',
  'admin.agents.tabUsers': 'User Access',
  'admin.agents.sections': 'Agent management sections',
  'admin.agents.loadFailed': 'Failed to load agents.',
  'admin.agents.agentUsersFailed': 'Failed to load agent users',
  'admin.agents.userAgentsFailed': 'Failed to load user agents',
  'admin.agents.webhookRequired': 'Webhook URL is required',
  'admin.agents.endpointRequired': 'Endpoint URL is required',
  'admin.agents.arnRequired': 'Agent Runtime ARN is required',
  'admin.agents.promptTooLong': 'Suggested prompts must be {n} characters or fewer.',
  'admin.agents.promptMax': 'A maximum of {n} suggested prompts is allowed.',
  'admin.agents.promptCount': 'A suggested prompts list needs either zero entries or between 2 and 4 entries.',
  'admin.agents.updatedTitle': 'Agent updated',
  'admin.agents.updatedBody': '"{name}" was updated.',
  'admin.agents.createdTitle': 'Agent created',
  'admin.agents.createdBody': '"{name}" is ready to use.',
  'admin.agents.unexpected': 'An unexpected error occurred. Please try again later.',
  'admin.agents.deleteTitle': 'Delete "{name}"?',
  'admin.agents.deleteMessage': 'This agent will be removed from the system immediately.',
  'admin.agents.deleteConsequence': 'Everyone currently assigned to this agent loses access, and its chat history is no longer reachable. This cannot be undone.',
  'admin.agents.deleteConfirm': 'Delete agent',
  'admin.agents.deletedTitle': 'Agent deleted',
  'admin.agents.deletedBody': '"{name}" was deleted.',
  'admin.agents.deleteFailed': 'Failed to delete agent',
  'admin.agents.deactivateTitle': 'Deactivate "{name}"?',
  'admin.agents.deactivateMessage': 'Users will no longer be able to chat with this agent until it is reactivated.',
  'admin.agents.nowActive': '"{name}" is now active.',
  'admin.agents.nowInactive': '"{name}" is now inactive.',
  'admin.agents.statusFailed': 'Failed to update status',
  'admin.agents.revokeTitle': 'Revoke access?',
  'admin.agents.revokeMessage': '{user} will no longer be able to use "{agent}".',
  'admin.agents.revokeConfirm': 'Revoke access',
  'admin.agents.grantedTitle': 'Access granted',
  'admin.agents.grantedBody': '{user} can now use "{agent}".',
  'admin.agents.grantFailed': 'Failed to grant access',
  'admin.agents.revokedTitle': 'Access revoked',
  'admin.agents.revokedBody': '{user} no longer has access.',
  'admin.agents.revokedFromBody': '{user} no longer has access to "{agent}".',
  'admin.agents.revokeFailed': 'Failed to revoke access',
  'admin.agents.searchPlaceholder': 'Search agents by name or description',
  'admin.agents.searchLabel': 'Search agents',
  'admin.agents.loading': 'Loading agents',
  'admin.agents.noMatch': 'No agents match "{q}"',
  'admin.agents.none': 'No agents found',
  'admin.agents.addFirst': '+ Add the first agent',
  'admin.agents.colIcon': 'Icon',
  'admin.agents.colUrl': 'Webhook URL',
  'admin.agents.colAuth': 'Auth',
  'admin.agents.colVisibility': 'Visibility',
  'admin.agents.notConfigured': 'Not configured',
  'admin.agents.invalidConfig': 'Invalid config',
  'admin.agents.urlLabel': 'Webhook URL: {url}',
  'admin.agents.authOn': 'Basic Auth configured',
  'admin.agents.authOff': 'No authentication',
  'admin.agents.public': 'Public',
  'admin.agents.publicHint': 'Public - all authenticated users',
  'admin.agents.perUser': 'Private',
  'admin.agents.perUserHint': 'Private - only granted users can see or use it',
  'admin.agents.restricted': 'Restricted',
  'admin.agents.restrictedHint': 'Restricted - everyone can see it, only granted users can use it',
  'admin.agents.descriptionLabel': 'Description: {text}',
  'admin.agents.access': 'Access',
  'admin.agents.noun': 'agents',
  'admin.agents.loadingUsers': 'Loading users',
  'admin.agents.noUserMatch': 'No users match "{q}"',
  'admin.agents.noUsers': 'No users found',
  'admin.agents.manageAccess': 'Manage Access',
  'admin.agents.manageAccessFor': 'Manage access for {name}',
  'admin.agents.editTitle': 'Edit Agent',
  'admin.agents.addTitle': 'Add New Agent',
  'admin.agents.name': 'Agent Name *',
  'admin.agents.namePlaceholder': 'e.g., IT Policy & Procedures Agent',
  'admin.agents.nameHint': 'A friendly name for your AI agent',
  'admin.agents.serviceType': 'Service Type *',
  'admin.agents.svcN8n': 'N8N Webhook',
  'admin.agents.svcGeneric': 'Generic HTTP Agent (Strands / Custom)',
  'admin.agents.svcAgentcore': 'AWS Bedrock AgentCore',
  'admin.agents.svcN8nHint': 'Routes through the N8N webhook service with n8n-specific payload format',
  'admin.agents.svcAgentcoreHint': 'Connects to an agent deployed on AWS Bedrock AgentCore (SigV4 signed)',
  'admin.agents.svcGenericHint': 'Sends a standard JSON payload to any HTTP agent (FastAPI, Flask, etc.)',
  'admin.agents.arn': 'Agent Runtime ARN *',
  'admin.agents.arnHint': 'The ARN of the deployed AgentCore runtime agent. AWS credentials are configured in appsettings.json.',
  'admin.agents.webhookUrl': 'N8N Webhook URL *',
  'admin.agents.webhookHint': 'The N8N webhook URL that handles chat messages',
  'admin.agents.endpointUrl': 'Chat Endpoint URL *',
  'admin.agents.endpointHint': 'The agent’s non-streaming chat endpoint',
  'admin.agents.streamUrl': 'Streaming Endpoint URL (Optional)',
  'admin.agents.streamHint': 'SSE streaming endpoint. If empty, the chat endpoint is used for both.',
  'admin.agents.agentType': 'Agent Type',
  'admin.agents.typeN8n': 'N8N Agent',
  'admin.agents.typeStrands': 'Strands Agent',
  'admin.agents.typeAgentcore': 'AgentCore Agent',
  'admin.agents.typeSpecialized': 'Specialized Agent',
  'admin.agents.agentTypeHint': 'The category or type of this agent',
  'admin.agents.auth': 'Authentication (Basic Auth)',
  'admin.agents.configured': 'Configured',
  'admin.agents.authHintN8n': 'Optional: Add Basic Auth credentials if your n8n webhook requires authentication',
  'admin.agents.authHintGeneric': 'Optional: Add Basic Auth credentials if your agent API requires authentication',
  'admin.agents.username': 'Username',
  'admin.agents.usernameLabel': 'Basic auth username',
  'admin.agents.password': 'Password',
  'admin.agents.passwordKeep': '••••••••  (leave empty to keep current)',
  'admin.agents.passwordLabel': 'Basic auth password',
  'admin.agents.showPassword': 'Show password',
  'admin.agents.hidePassword': 'Hide password',
  'admin.agents.keepPasswordHint': 'Leave password empty to keep the existing credentials',
  'admin.agents.mcp': 'MCP Connector Credentials',
  'admin.agents.mcpToggle': 'Send users’ connector access tokens to this agent',
  'admin.agents.mcpHint': 'Only enable for agents you control. When on, this agent receives the decrypted tokens for every connector a user has switched on — Google Drive, GitHub, Slack, and so on. Leave off and the agent still sees which connectors exist, but not their secrets.',
  'admin.agents.mcpWarning': 'Tokens are only sent over HTTPS endpoints. Anyone who controls this endpoint can read the third-party credentials of every user who chats with this agent.',
  'admin.agents.tools': 'Available Tools (for Data Scoping)',
  'admin.agents.toolsHint': 'Define the tool names available in this agent’s workflow. These will appear as options when creating tool scopes.',
  'admin.agents.toolPlaceholder': 'e.g., Nesma Documents Vector Store',
  'admin.agents.addItem': '+ Add',
  'admin.agents.removeTool': 'Remove tool',
  'admin.agents.removeToolName': 'Remove tool {name}',
  'admin.agents.prompts': 'Suggested Prompts',
  'admin.agents.promptsHint': 'Add 2 to 4 prompts for the chat welcome screen, up to 150 characters each. Leave empty to use the default prompts.',
  'admin.agents.promptPlaceholder': 'e.g., Summarize this document',
  'admin.agents.removePrompt': 'Remove suggested prompt',
  'admin.agents.removePromptText': 'Remove suggested prompt: {text}',
  'admin.agents.description': 'Description (Optional)',
  'admin.agents.descriptionPlaceholder': 'Brief description of what this agent does...',
  'admin.agents.systemPrompt': 'System Prompt',
  'admin.agents.systemPromptHint': 'Instructions stored with this agent. Leave empty to use “{prompt}”',
  'admin.agents.avatarColor': 'Avatar Color (Optional)',
  'admin.agents.resetColor': 'Reset to default',
  'admin.agents.avatarIcon': 'Avatar Icon',
  'admin.agents.avatarIconHint': '“Auto” picks an icon from the agent’s name. The preview above updates as you choose.',
  'admin.agents.activeToggle': 'Active (users can access this agent)',
  'admin.agents.visibility': 'Visibility Mode',
  'admin.agents.visPerUser': 'Private (only granted users can see and use it)',
  'admin.agents.visRestricted': 'Restricted (everyone sees it, only granted users can use it)',
  'admin.agents.visPublic': 'Public (all authenticated users)',
  'admin.agents.visibilityHint': 'Public agents are open to everyone. Restricted agents are listed for everyone but locked until you grant access. Private agents stay hidden from anyone without a grant, which suits agents still in testing.',
  'admin.agents.update': 'Update Agent',
  'admin.agents.create': 'Create Agent',
  'admin.agents.manageTitle': 'Manage Access: {name}',
  'admin.agents.isPublic': 'This agent is public',
  'admin.agents.isPublicRest': '— all authenticated users have access.',
  'admin.agents.isPublicHint': 'Individual access grants below will take effect if this agent is switched to restricted or private.',
  'admin.agents.isRestricted': 'This agent is restricted',
  'admin.agents.isRestrictedRest': '— everyone can see it, but only the users below can use it.',
  'admin.agents.usersWithAccess': 'Users with Access',
  'admin.agents.noUsersAccess': 'No users have access yet',
  'admin.agents.departmentsWithAccess': 'Departments with Access',
  'admin.agents.departmentsHint': 'Everyone in a granted department can use this agent, including people who join it later.',
  'admin.agents.noDepartmentsAccess': 'No departments have access yet',
  'admin.agents.deptInactive': 'Inactive (no access)',
  'admin.agents.deptMembers': '{n} members',
  'admin.agents.grantToDepartment': 'Grant access to a department',
  'admin.agents.pickDepartment': 'Choose a department…',
  'admin.agents.deptOption': '{name} ({n} members)',
  'admin.agents.allDepartmentsHave': 'All departments already have access',
  'admin.agents.agentDepartmentsFailed': 'Failed to load department access',
  'admin.agents.deptGrantedBody': '{department} ({n} members) can now use {agent}',
  'admin.agents.deptRevokeMessage': 'Remove {department}’s access to {agent}? Members with their own individual grant keep it.',
  'admin.agents.deptRevokedBody': 'Access removed for {department}',
  'admin.agents.grantedOn': 'Granted: {date}',
  'admin.agents.grantedOnBy': 'Granted: {date} by {by}',
  'admin.agents.revoke': 'Revoke',
  'admin.agents.revokeFor': 'Revoke access for {name}',
  'admin.agents.grantToUser': 'Grant Access to User',
  'admin.agents.filterUsers': 'Filter users by name or email',
  'admin.agents.filterUsersLabel': 'Filter users to grant access',
  'admin.agents.grant': 'Grant Access',
  'admin.agents.grantTo': 'Grant access to {name}',
  'admin.agents.allUsersHave': 'All users have access or no users available',
  'admin.agents.agentsWithAccess': 'Agents with Access',
  'admin.agents.noAgentsAccess': 'No agents accessible yet',
  'admin.agents.revokeTo': 'Revoke access to {name}',
  'admin.agents.grantToAgent': 'Grant Access to Agent',
  'admin.agents.filterAgents': 'Filter agents by name or description',
  'admin.agents.filterAgentsLabel': 'Filter agents to grant access',
  'admin.agents.allAgentsHave': 'All agents accessible or no agents available',
};

const ar: Record<keyof typeof en, string> = {
  'admin.role.Admin': 'مسؤول',
  'admin.role.User': 'مستخدم',
  'admin.allStatus': 'كل الحالات',
  'admin.filterByStatus': 'التصفية حسب الحالة',
  'admin.operationFailed': 'تعذّر إتمام العملية',
  'admin.noDepartment': 'بدون قسم',
  'admin.createdCol': 'تاريخ الإنشاء',
  'admin.statusCol': 'الحالة',
  'admin.nameCol': 'الاسم',
  'admin.descriptionCol': 'الوصف',

  'admin.users.title': 'إدارة المستخدمين',
  'admin.users.subtitle': 'إدارة مستخدمي النظام وصلاحياتهم',
  'admin.users.add': '+ إضافة مستخدم جديد',
  'admin.users.searchPlaceholder': 'ابحث بالاسم أو البريد الإلكتروني',
  'admin.users.searchLabel': 'البحث عن المستخدمين بالاسم أو البريد الإلكتروني',
  'admin.users.filterRole': 'التصفية حسب الدور',
  'admin.users.allRoles': 'كل الأدوار',
  'admin.users.filterDepartment': 'التصفية حسب القسم',
  'admin.users.allDepartments': 'كل الأقسام',
  'admin.users.loading': 'جارٍ تحميل المستخدمين',
  'admin.users.loadFailed': 'تعذّر تحميل المستخدمين.',
  'admin.users.noMatch': 'لا يوجد مستخدمون يطابقون عوامل التصفية.',
  'admin.users.none': 'لا يوجد مستخدمون بعد.',
  'admin.users.addFirst': '+ إضافة أول مستخدم',
  'admin.users.colEmail': 'البريد الإلكتروني',
  'admin.users.colRole': 'الدور',
  'admin.users.colDepartment': 'القسم',
  'admin.users.edit': 'تعديل {name}',
  'admin.users.activate': 'تفعيل',
  'admin.users.deactivate': 'إيقاف',
  'admin.users.activateName': 'تفعيل {name}',
  'admin.users.deactivateName': 'إيقاف {name}',
  'admin.users.deleteName': 'حذف {name}',
  'admin.users.noun': 'مستخدمين',
  'admin.users.editTitle': 'تعديل المستخدم',
  'admin.users.addTitle': 'إضافة مستخدم جديد',
  'admin.users.fullName': 'الاسم الكامل',
  'admin.users.email': 'البريد الإلكتروني',
  'admin.users.password': 'كلمة المرور',
  'admin.users.passwordKeep': '(اتركها فارغة للإبقاء على الحالية)',
  'admin.users.passwordHint': '6 أحرف على الأقل.',
  'admin.users.role': 'الدور',
  'admin.users.department': 'القسم',
  'admin.users.activeCheckbox': 'نشط',
  'admin.access.selfNote': 'لا يمكنك تغيير صلاحياتك على المهام أو سير العمل أو الموصلات. يمكن لمسؤول آخر ذلك.',
  'admin.access.adminHideNote': 'هذا الحساب مسؤول. يحصل المسؤولون على هذه الميزات افتراضيًا؛ ألغِ تحديد أي منها لإخفائها عنه.',
  'admin.connectors.section': 'الموصلات (MCP)',
  'admin.connectors.enable': 'السماح لهذا المستخدم بربط خوادم MCP (Google Drive وOneDrive وخوادم مخصصة) ليستخدمها الوكلاء',
  'admin.connectors.hint': 'يضيف الموصلات ويديرها من شريط كتابة الرسائل. إيقاف الوصول يُبقي موصلاته المحفوظة، لكن الوكلاء والمهام يتوقفون عن استخدامها.',
  'admin.connectors.inUse': '{n} موصل محفوظ.',
  'admin.connectors.updateFailed': 'تم حفظ المستخدم، لكن تعذّر تحديث صلاحية الموصلات.',
  'admin.users.update': 'تحديث المستخدم',
  'admin.users.create': 'إنشاء المستخدم',
  'admin.users.updatedTitle': 'تم تحديث المستخدم',
  'admin.users.updatedBody': 'تم تحديث بيانات {name}.',
  'admin.users.createdTitle': 'تم إنشاء المستخدم',
  'admin.users.createdBody': 'يمكن لـ {name} تسجيل الدخول الآن.',
  'admin.users.deleteTitle': 'حذف {name}؟',
  'admin.users.deleteMessage': 'ستتم إزالة هذا الحساب من النظام.',
  'admin.users.deleteConsequence': 'سيفقد المستخدم الوصول إلى جميع الوكلاء فوراً ولن يتمكن من تسجيل الدخول. لا يمكن التراجع عن ذلك.',
  'admin.users.deleteConfirm': 'حذف المستخدم',
  'admin.users.deletedTitle': 'تم حذف المستخدم',
  'admin.users.deletedBody': 'تم حذف {name}.',
  'admin.users.deleteFailed': 'تعذّر حذف المستخدم',
  'admin.users.deactivateTitle': 'إيقاف {name}؟',
  'admin.users.deactivateMessage': 'سيتم تسجيل خروج المستخدم ومنعه من تسجيل الدخول حتى إعادة تفعيله.',
  'admin.users.nowActive': '{name} نشط الآن.',
  'admin.users.nowInactive': '{name} غير نشط الآن.',
  'admin.users.statusFailed': 'تعذّر تحديث الحالة',

  'admin.depts.title': 'الأقسام',
  'admin.depts.subtitle': 'نظّم الموظفين في أقسام واعرف من ينتمي إلى كل قسم',
  'admin.depts.add': '+ إضافة قسم',
  'admin.depts.search': 'البحث في الأقسام',
  'admin.depts.loadFailed': 'تعذّر تحميل الأقسام.',
  'admin.depts.loading': 'جارٍ تحميل الأقسام',
  'admin.depts.noMatch': 'لا توجد أقسام تطابق عوامل التصفية.',
  'admin.depts.none': 'لا توجد أقسام بعد. أنشئ قسماً لبدء تنظيم المستخدمين.',
  'admin.depts.addFirst': '+ إضافة أول قسم',
  'admin.depts.colDepartment': 'القسم',
  'admin.depts.colManager': 'المدير',
  'admin.depts.colMembers': 'الأعضاء',
  'admin.depts.viewMembers': 'عرض الأعضاء',
  'admin.depts.memberOne': 'عضو واحد',
  'admin.depts.memberMany': '{n} أعضاء',
  'admin.depts.showMembers': 'عرض أعضاء {name}',
  'admin.depts.hideMembers': 'إخفاء أعضاء {name}',
  'admin.depts.editName': 'تعديل {name}',
  'admin.depts.deleteName': 'حذف {name}',
  'admin.depts.loadingMembers': 'جارٍ تحميل الأعضاء',
  'admin.depts.noMembers': 'لا يوجد مستخدمون في هذا القسم.',
  'admin.depts.noun': 'أقسام',
  'admin.depts.editTitle': 'تعديل القسم',
  'admin.depts.addTitle': 'إضافة قسم',
  'admin.depts.nameLabel': 'اسم القسم',
  'admin.depts.namePlaceholder': 'مثال: تقنية المعلومات، المالية، الموارد البشرية',
  'admin.depts.descLabel': 'الوصف',
  'admin.depts.descPlaceholder': 'وصف مختصر (اختياري)',
  'admin.depts.managerLabel': 'المدير (اختياري)',
  'admin.depts.noManager': 'بدون مدير',
  'admin.depts.update': 'تحديث القسم',
  'admin.depts.create': 'إنشاء القسم',
  'admin.depts.updatedTitle': 'تم تحديث القسم',
  'admin.depts.updatedBody': 'تم تحديث {name}.',
  'admin.depts.createdTitle': 'تم إنشاء القسم',
  'admin.depts.createdBody': 'تم إنشاء {name}.',
  'admin.depts.deleteTitle': 'حذف "{name}"؟',
  'admin.depts.deleteMessage': 'ستتم إزالة القسم من الهيكل التنظيمي.',
  'admin.depts.deleteConsequenceOne': 'سيصبح العضو الوحيد في القسم دون قسم. لن يُحذف المستخدم، لكنه سيفقد أي صلاحيات ممنوحة عبر هذا القسم.',
  'admin.depts.deleteConsequenceMany': 'سيصبح أعضاء القسم وعددهم {n} دون قسم. لن يُحذف المستخدمون، لكنهم سيفقدون أي صلاحيات ممنوحة عبر هذا القسم.',
  'admin.depts.deleteConfirm': 'حذف القسم',
  'admin.depts.deletedTitle': 'تم حذف القسم',
  'admin.depts.deletedBody': 'تم حذف {name}.',
  'admin.depts.deleteFailed': 'تعذّر حذف القسم',
  'admin.depts.membersFailed': 'تعذّر تحميل أعضاء القسم',

  'admin.fb.range7': 'آخر 7 أيام',
  'admin.fb.range30': 'آخر 30 يوماً',
  'admin.fb.range90': 'آخر 90 يوماً',
  'admin.fb.range365': 'آخر سنة',
  'admin.fb.timeRange': 'الفترة الزمنية',
  'admin.fb.tabNeedsReview': 'بحاجة إلى مراجعة',
  'admin.fb.tabNegative': 'سلبية',
  'admin.fb.tabPositive': 'إيجابية',
  'admin.fb.tabReviewed': 'تمت مراجعتها',
  'admin.fb.tabAll': 'الكل',
  'admin.fb.negative': 'سلبي',
  'admin.fb.positive': 'إيجابي',
  'admin.fb.question': 'السؤال',
  'admin.fb.response': 'الرد',
  'admin.fb.showLess': 'عرض أقل',
  'admin.fb.showFull': 'عرض الرد كاملاً',
  'admin.fb.reviewed': 'تمت المراجعة',
  'admin.fb.reviewedBy': 'راجعه {name}',
  'admin.fb.reopen': 'إعادة فتح',
  'admin.fb.noteLabel': 'ملاحظة المراجعة',
  'admin.fb.notePlaceholder': 'ملاحظة اختيارية (مثل: السؤال غير واضح، المصدر مفقود)…',
  'admin.fb.markReviewed': 'تحديد كمُراجَع',
  'admin.fb.loadFailed': 'تعذّر تحميل التقييمات.',
  'admin.fb.markedToast': 'تم التحديد كمُراجَع.',
  'admin.fb.reopenedToast': 'أُعيد فتحه للمراجعة.',
  'admin.fb.updateFailed': 'تعذّر تحديث المراجعة.',
  'admin.fb.title': 'مراجعة التقييمات',
  'admin.fb.subtitle': 'اطّلع على تقييمات المستخدمين وتابع التقييمات السلبية التي تمت مراجعتها',
  'admin.fb.tablist': 'تصفية التقييمات',
  'admin.fb.searchPlaceholder': 'ابحث في السؤال أو الرد أو المستخدم…',
  'admin.fb.searchLabel': 'البحث في التقييمات',
  'admin.fb.filterAgent': 'التصفية حسب الوكيل',
  'admin.fb.allAgents': 'كل الوكلاء',
  'admin.fb.loading': 'جارٍ تحميل التقييمات',
  'admin.fb.noMatch': 'لا توجد تقييمات تطابق عوامل التصفية.',
  'admin.fb.nothingToReview': 'لا يوجد ما يحتاج إلى مراجعة في هذه الفترة.',
  'admin.fb.none': 'لا توجد تقييمات في هذه الفترة.',
  'admin.fb.noun': 'تقييمات',
  'admin.fb.chart': '[رسم بياني]',


  'admin.icon.auto': 'تلقائي (حسب الاسم)',
  'admin.icon.autoTag': 'تلقائي',
  'admin.icon.sparkles': 'عام',
  'admin.icon.scale': 'الشؤون القانونية',
  'admin.icon.shield': 'الأمن',
  'admin.icon.landmark': 'المالية',
  'admin.icon.calculator': 'المحاسبة',
  'admin.icon.brain': 'المعرفة',
  'admin.icon.hardhat': 'السلامة',
  'admin.icon.users': 'الموارد البشرية',
  'admin.icon.cart': 'المشتريات',
  'admin.icon.truck': 'الخدمات اللوجستية',
  'admin.icon.wrench': 'الصيانة',
  'admin.icon.building': 'المشاريع',
  'admin.icon.megaphone': 'التسويق',
  'admin.icon.graduation': 'التدريب',
  'admin.icon.heart': 'الصحة والعافية',
  'admin.icon.file': 'المستندات',
  'admin.icon.chart': 'التحليلات',
  'admin.icon.globe': 'الشؤون الدولية',
  'admin.icon.briefcase': 'الأعمال',
  'admin.icon.headset': 'الدعم',
  'admin.icon.code': 'التطوير',
  'admin.icon.database': 'البيانات',

  'admin.agents.title': 'إدارة الوكلاء',
  'admin.agents.subtitle': 'إدارة وكلاء الذكاء الاصطناعي وصلاحيات وصول المستخدمين',
  'admin.agents.add': '+ إضافة وكيل جديد',
  'admin.agents.tabAgents': 'الوكلاء',
  'admin.agents.tabUsers': 'صلاحيات المستخدمين',
  'admin.agents.sections': 'أقسام إدارة الوكلاء',
  'admin.agents.loadFailed': 'تعذّر تحميل الوكلاء.',
  'admin.agents.agentUsersFailed': 'تعذّر تحميل مستخدمي الوكيل',
  'admin.agents.userAgentsFailed': 'تعذّر تحميل وكلاء المستخدم',
  'admin.agents.webhookRequired': 'عنوان Webhook مطلوب',
  'admin.agents.endpointRequired': 'عنوان نقطة النهاية مطلوب',
  'admin.agents.arnRequired': 'معرّف ARN لبيئة تشغيل الوكيل مطلوب',
  'admin.agents.promptTooLong': 'يجب ألا يتجاوز كل اقتراح {n} حرفاً.',
  'admin.agents.promptMax': 'الحد الأقصى {n} اقتراحات.',
  'admin.agents.promptCount': 'يجب أن تكون قائمة الاقتراحات فارغة أو تضم من 2 إلى 4 اقتراحات.',
  'admin.agents.updatedTitle': 'تم تحديث الوكيل',
  'admin.agents.updatedBody': 'تم تحديث "{name}".',
  'admin.agents.createdTitle': 'تم إنشاء الوكيل',
  'admin.agents.createdBody': '"{name}" جاهز للاستخدام.',
  'admin.agents.unexpected': 'حدث خطأ غير متوقع. يُرجى المحاولة لاحقاً.',
  'admin.agents.deleteTitle': 'حذف "{name}"؟',
  'admin.agents.deleteMessage': 'ستتم إزالة هذا الوكيل من النظام فوراً.',
  'admin.agents.deleteConsequence': 'سيفقد كل المستخدمين المعيّنين لهذا الوكيل صلاحية الوصول، ولن يكون سجل محادثاته متاحاً بعد ذلك. لا يمكن التراجع عن هذا الإجراء.',
  'admin.agents.deleteConfirm': 'حذف الوكيل',
  'admin.agents.deletedTitle': 'تم حذف الوكيل',
  'admin.agents.deletedBody': 'تم حذف "{name}".',
  'admin.agents.deleteFailed': 'تعذّر حذف الوكيل',
  'admin.agents.deactivateTitle': 'تعطيل "{name}"؟',
  'admin.agents.deactivateMessage': 'لن يتمكن المستخدمون من محادثة هذا الوكيل حتى تتم إعادة تفعيله.',
  'admin.agents.nowActive': '"{name}" مفعّل الآن.',
  'admin.agents.nowInactive': '"{name}" معطّل الآن.',
  'admin.agents.statusFailed': 'تعذّر تحديث الحالة',
  'admin.agents.revokeTitle': 'إلغاء الصلاحية؟',
  'admin.agents.revokeMessage': 'لن يتمكن {user} من استخدام "{agent}" بعد الآن.',
  'admin.agents.revokeConfirm': 'إلغاء الصلاحية',
  'admin.agents.grantedTitle': 'تم منح الصلاحية',
  'admin.agents.grantedBody': 'يمكن لـ {user} الآن استخدام "{agent}".',
  'admin.agents.grantFailed': 'تعذّر منح الصلاحية',
  'admin.agents.revokedTitle': 'تم إلغاء الصلاحية',
  'admin.agents.revokedBody': 'لم يعد لدى {user} صلاحية الوصول.',
  'admin.agents.revokedFromBody': 'لم يعد لدى {user} صلاحية الوصول إلى "{agent}".',
  'admin.agents.revokeFailed': 'تعذّر إلغاء الصلاحية',
  'admin.agents.searchPlaceholder': 'ابحث عن الوكلاء بالاسم أو الوصف',
  'admin.agents.searchLabel': 'البحث في الوكلاء',
  'admin.agents.loading': 'جارٍ تحميل الوكلاء',
  'admin.agents.noMatch': 'لا يوجد وكلاء يطابقون "{q}"',
  'admin.agents.none': 'لا يوجد وكلاء',
  'admin.agents.addFirst': '+ أضف أول وكيل',
  'admin.agents.colIcon': 'الأيقونة',
  'admin.agents.colUrl': 'عنوان Webhook',
  'admin.agents.colAuth': 'المصادقة',
  'admin.agents.colVisibility': 'الظهور',
  'admin.agents.notConfigured': 'غير مُعدّ',
  'admin.agents.invalidConfig': 'إعدادات غير صالحة',
  'admin.agents.urlLabel': 'عنوان Webhook: {url}',
  'admin.agents.authOn': 'تم إعداد المصادقة الأساسية',
  'admin.agents.authOff': 'بلا مصادقة',
  'admin.agents.public': 'عام',
  'admin.agents.publicHint': 'عام - لجميع المستخدمين المسجّلين',
  'admin.agents.perUser': 'خاص',
  'admin.agents.perUserHint': 'خاص - لا يراه أو يستخدمه إلا من مُنح الصلاحية',
  'admin.agents.restricted': 'مقيّد',
  'admin.agents.restrictedHint': 'مقيّد - يراه الجميع، ولا يستخدمه إلا من مُنح الصلاحية',
  'admin.agents.descriptionLabel': 'الوصف: {text}',
  'admin.agents.access': 'الصلاحيات',
  'admin.agents.noun': 'وكلاء',
  'admin.agents.loadingUsers': 'جارٍ تحميل المستخدمين',
  'admin.agents.noUserMatch': 'لا يوجد مستخدمون يطابقون "{q}"',
  'admin.agents.noUsers': 'لا يوجد مستخدمون',
  'admin.agents.manageAccess': 'إدارة الصلاحيات',
  'admin.agents.manageAccessFor': 'إدارة صلاحيات {name}',
  'admin.agents.editTitle': 'تعديل الوكيل',
  'admin.agents.addTitle': 'إضافة وكيل جديد',
  'admin.agents.name': 'اسم الوكيل *',
  'admin.agents.namePlaceholder': 'مثال: وكيل سياسات وإجراءات تقنية المعلومات',
  'admin.agents.nameHint': 'اسم واضح لوكيل الذكاء الاصطناعي',
  'admin.agents.serviceType': 'نوع الخدمة *',
  'admin.agents.svcN8n': 'Webhook من N8N',
  'admin.agents.svcGeneric': 'وكيل HTTP عام (Strands / مخصص)',
  'admin.agents.svcAgentcore': 'AWS Bedrock AgentCore',
  'admin.agents.svcN8nHint': 'يمر عبر خدمة Webhook من N8N بصيغة الحمولة الخاصة بـ n8n',
  'admin.agents.svcAgentcoreHint': 'يتصل بوكيل منشور على AWS Bedrock AgentCore (موقّع بـ SigV4)',
  'admin.agents.svcGenericHint': 'يرسل حمولة JSON قياسية إلى أي وكيل HTTP (مثل FastAPI وFlask)',
  'admin.agents.arn': 'معرّف ARN لبيئة تشغيل الوكيل *',
  'admin.agents.arnHint': 'معرّف ARN لوكيل AgentCore المنشور. تُضبط بيانات اعتماد AWS في appsettings.json.',
  'admin.agents.webhookUrl': 'عنوان Webhook من N8N *',
  'admin.agents.webhookHint': 'عنوان Webhook من N8N الذي يعالج رسائل المحادثة',
  'admin.agents.endpointUrl': 'عنوان نقطة نهاية المحادثة *',
  'admin.agents.endpointHint': 'نقطة نهاية المحادثة غير المتدفقة للوكيل',
  'admin.agents.streamUrl': 'عنوان نقطة نهاية البث (اختياري)',
  'admin.agents.streamHint': 'نقطة نهاية بث SSE. إن تُركت فارغة تُستخدم نقطة نهاية المحادثة لكليهما.',
  'admin.agents.agentType': 'نوع الوكيل',
  'admin.agents.typeN8n': 'وكيل N8N',
  'admin.agents.typeStrands': 'وكيل Strands',
  'admin.agents.typeAgentcore': 'وكيل AgentCore',
  'admin.agents.typeSpecialized': 'وكيل متخصص',
  'admin.agents.agentTypeHint': 'فئة هذا الوكيل أو نوعه',
  'admin.agents.auth': 'المصادقة (Basic Auth)',
  'admin.agents.configured': 'مُعدّة',
  'admin.agents.authHintN8n': 'اختياري: أضف بيانات اعتماد Basic Auth إذا كان Webhook الخاص بـ n8n يتطلب المصادقة',
  'admin.agents.authHintGeneric': 'اختياري: أضف بيانات اعتماد Basic Auth إذا كانت واجهة الوكيل تتطلب المصادقة',
  'admin.agents.username': 'اسم المستخدم',
  'admin.agents.usernameLabel': 'اسم مستخدم المصادقة الأساسية',
  'admin.agents.password': 'كلمة المرور',
  'admin.agents.passwordKeep': '••••••••  (اتركها فارغة للإبقاء على الحالية)',
  'admin.agents.passwordLabel': 'كلمة مرور المصادقة الأساسية',
  'admin.agents.showPassword': 'إظهار كلمة المرور',
  'admin.agents.hidePassword': 'إخفاء كلمة المرور',
  'admin.agents.keepPasswordHint': 'اترك كلمة المرور فارغة للإبقاء على بيانات الاعتماد الحالية',
  'admin.agents.mcp': 'بيانات اعتماد موصلات MCP',
  'admin.agents.mcpToggle': 'إرسال رموز وصول موصلات المستخدمين إلى هذا الوكيل',
  'admin.agents.mcpHint': 'فعّل هذا الخيار للوكلاء الذين تتحكم بهم فقط. عند تفعيله يتلقى الوكيل الرموز المفكوكة التشفير لكل موصل فعّله المستخدم، مثل Google Drive وGitHub وSlack. وعند إيقافه يرى الوكيل الموصلات الموجودة دون أسرارها.',
  'admin.agents.mcpWarning': 'لا تُرسل الرموز إلا عبر نقاط نهاية HTTPS. ويستطيع أي شخص يتحكم في نقطة النهاية هذه قراءة بيانات اعتماد الجهات الخارجية لكل مستخدم يحادث هذا الوكيل.',
  'admin.agents.tools': 'الأدوات المتاحة (لحوكمة البيانات)',
  'admin.agents.toolsHint': 'حدد أسماء الأدوات المتاحة في سير عمل هذا الوكيل. ستظهر خياراتٍ عند إنشاء نطاقات الأدوات.',
  'admin.agents.toolPlaceholder': 'مثال: Nesma Documents Vector Store',
  'admin.agents.addItem': '+ إضافة',
  'admin.agents.removeTool': 'إزالة الأداة',
  'admin.agents.removeToolName': 'إزالة الأداة {name}',
  'admin.agents.prompts': 'الاقتراحات المقترحة',
  'admin.agents.promptsHint': 'أضف من 2 إلى 4 اقتراحات لشاشة الترحيب في المحادثة، بحد أقصى 150 حرفاً لكل منها. اتركها فارغة لاستخدام الاقتراحات الافتراضية.',
  'admin.agents.promptPlaceholder': 'مثال: لخّص هذا المستند',
  'admin.agents.removePrompt': 'إزالة الاقتراح',
  'admin.agents.removePromptText': 'إزالة الاقتراح: {text}',
  'admin.agents.description': 'الوصف (اختياري)',
  'admin.agents.descriptionPlaceholder': 'وصف موجز لمهمة هذا الوكيل...',
  'admin.agents.systemPrompt': 'موجّه النظام',
  'admin.agents.systemPromptHint': 'تعليمات محفوظة مع هذا الوكيل. اتركها فارغة لاستخدام “{prompt}”',
  'admin.agents.avatarColor': 'لون الصورة الرمزية (اختياري)',
  'admin.agents.resetColor': 'استعادة الافتراضي',
  'admin.agents.avatarIcon': 'أيقونة الصورة الرمزية',
  'admin.agents.avatarIconHint': 'يختار خيار “تلقائي” أيقونة بناءً على اسم الوكيل. تتحدث المعاينة أعلاه أثناء الاختيار.',
  'admin.agents.activeToggle': 'نشط (يمكن للمستخدمين الوصول إلى هذا الوكيل)',
  'admin.agents.visibility': 'وضع الظهور',
  'admin.agents.visPerUser': 'خاص (لا يراه أو يستخدمه إلا من مُنح الصلاحية)',
  'admin.agents.visRestricted': 'مقيّد (يراه الجميع، ولا يستخدمه إلا من مُنح الصلاحية)',
  'admin.agents.visPublic': 'عام (جميع المستخدمين المسجّلين)',
  'admin.agents.visibilityHint': 'الوكلاء العامّون متاحون للجميع. الوكلاء المقيّدون يظهرون للجميع لكنهم مقفلون حتى تمنح الصلاحية. الوكلاء الخاصّون مخفيّون عن كل من لا يملك صلاحية، وهذا مناسب للوكلاء قيد الاختبار.',
  'admin.agents.update': 'تحديث الوكيل',
  'admin.agents.create': 'إنشاء الوكيل',
  'admin.agents.manageTitle': 'إدارة الصلاحيات: {name}',
  'admin.agents.isPublic': 'هذا الوكيل عام',
  'admin.agents.isPublicRest': '— لجميع المستخدمين المسجّلين صلاحية الوصول.',
  'admin.agents.isPublicHint': 'ستسري الصلاحيات الفردية أدناه إذا تم تحويل الوكيل إلى وضع مقيّد أو خاص.',
  'admin.agents.isRestricted': 'هذا الوكيل مقيّد',
  'admin.agents.isRestrictedRest': '— يمكن للجميع رؤيته، لكن لا يستخدمه إلا المستخدمون أدناه.',
  'admin.agents.usersWithAccess': 'المستخدمون ذوو الصلاحية',
  'admin.agents.noUsersAccess': 'لا يملك أي مستخدم صلاحية الوصول بعد',
  'admin.agents.departmentsWithAccess': 'الأقسام ذات الصلاحية',
  'admin.agents.departmentsHint': 'يمكن لكل أعضاء القسم الممنوح استخدام هذا الوكيل، بمن فيهم من ينضم إليه لاحقاً.',
  'admin.agents.noDepartmentsAccess': 'لا يملك أي قسم صلاحية الوصول بعد',
  'admin.agents.deptInactive': 'غير نشط (بلا صلاحية)',
  'admin.agents.deptMembers': '{n} أعضاء',
  'admin.agents.grantToDepartment': 'منح الصلاحية لقسم',
  'admin.agents.pickDepartment': 'اختر قسماً…',
  'admin.agents.deptOption': '{name} ({n} أعضاء)',
  'admin.agents.allDepartmentsHave': 'جميع الأقسام لديها صلاحية بالفعل',
  'admin.agents.agentDepartmentsFailed': 'تعذّر تحميل صلاحيات الأقسام',
  'admin.agents.deptGrantedBody': 'يمكن لقسم {department} ({n} أعضاء) الآن استخدام {agent}',
  'admin.agents.deptRevokeMessage': 'إزالة صلاحية قسم {department} على {agent}؟ يحتفظ الأعضاء الذين لديهم صلاحية فردية بها.',
  'admin.agents.deptRevokedBody': 'تمت إزالة صلاحية قسم {department}',
  'admin.agents.grantedOn': 'تاريخ المنح: {date}',
  'admin.agents.grantedOnBy': 'تاريخ المنح: {date} بواسطة {by}',
  'admin.agents.revoke': 'إلغاء',
  'admin.agents.revokeFor': 'إلغاء صلاحية {name}',
  'admin.agents.grantToUser': 'منح الصلاحية لمستخدم',
  'admin.agents.filterUsers': 'تصفية المستخدمين بالاسم أو البريد الإلكتروني',
  'admin.agents.filterUsersLabel': 'تصفية المستخدمين لمنح الصلاحية',
  'admin.agents.grant': 'منح الصلاحية',
  'admin.agents.grantTo': 'منح الصلاحية لـ {name}',
  'admin.agents.allUsersHave': 'جميع المستخدمين لديهم صلاحية أو لا يوجد مستخدمون متاحون',
  'admin.agents.agentsWithAccess': 'الوكلاء المتاحون',
  'admin.agents.noAgentsAccess': 'لا يوجد وكلاء متاحون بعد',
  'admin.agents.revokeTo': 'إلغاء الوصول إلى {name}',
  'admin.agents.grantToAgent': 'منح الوصول إلى وكيل',
  'admin.agents.filterAgents': 'تصفية الوكلاء بالاسم أو الوصف',
  'admin.agents.filterAgentsLabel': 'تصفية الوكلاء لمنح الصلاحية',
  'admin.agents.allAgentsHave': 'جميع الوكلاء متاحون أو لا يوجد وكلاء',
};

const admin = { en, ar };
export default admin;
